import { InstallPrompt } from '@/components/pwa/InstallPrompt'
import { OfflineIndicator } from '@/components/pwa/OfflineIndicator'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { lazy, Suspense, useEffect, type ReactNode, type ComponentType } from 'react'

/**
 * Detect whether an error is a chunk/module load failure (e.g. after deployment).
 */
function isChunkLoadError(err: Error): boolean {
  const msg = err.message.toLowerCase()
  return (
    msg.includes('dynamically imported module') ||
    msg.includes('failed to fetch') ||
    msg.includes('loading chunk') ||
    msg.includes('loading css chunk') ||
    msg.includes('error loading') ||
    msg.includes('load failed') ||
    msg.includes('importing a module script failed') ||
    err.name === 'ChunkLoadError'
  )
}

/**
 * Lazy import with automatic retry on chunk load failure.
 * After a new deployment, old chunk hashes no longer exist on the CDN.
 * This retries the import once, and if it fails again, forces a page reload
 * to fetch fresh HTML with new hashes.
 */
function lazyRetry<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
): React.LazyExoticComponent<T> {
  return lazy(() =>
    factory().catch((err: Error) => {
      if (!isChunkLoadError(err)) throw err

      // Retry the import once before reloading (handles transient network issues)
      return factory().catch((retryErr: Error) => {
        // Avoid infinite reload loops — only reload once per session
        const key = 'chunk_reload'
        if (!sessionStorage.getItem(key)) {
          sessionStorage.setItem(key, '1')
          window.location.reload()
          return new Promise<{ default: T }>(() => {})
        }
        throw retryErr
      })
    })
  )
}
import { Routes, Route, Navigate, useSearchParams, useLocation } from 'react-router-dom'
import { AuthProvider, useAuth } from '@/hooks/useAuth'
import { SubscriptionProvider } from '@/hooks/useSubscription'
import { BrandingProvider } from '@/contexts/BrandingContext'
import { ViewModeProvider } from '@/contexts/ViewModeContext'
import { OnboardingProvider } from '@/contexts/OnboardingContext'
import { ProtectedRoute } from '@/components/auth/ProtectedRoute'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { Toaster } from '@/components/ui/toaster'
const LoginPage = lazyRetry(() => import('@/pages/Login'))
const SignupPage = lazyRetry(() => import('@/pages/Signup'))
const ForgotPasswordPage = lazyRetry(() => import('@/pages/ForgotPassword'))
const ResetPasswordPage = lazyRetry(() => import('@/pages/ResetPassword'))
const AcceptInvitePage = lazyRetry(() => import('@/pages/AcceptInvite'))
const DashboardPage = lazyRetry(() => import('@/pages/Dashboard'))
const ProductsPage = lazyRetry(() => import('@/pages/Products'))
const ProductDetailPage = lazyRetry(() => import('@/pages/ProductDetail'))
const MarketRequirementsPage = lazyRetry(() => import('@/pages/MarketRequirements'))
const ChecklistsPage = lazyRetry(() => import('@/pages/Checklists'))
const ChecklistDetailPage = lazyRetry(() => import('@/pages/ChecklistDetail'))
const DocumentsEnhancedPage = lazyRetry(() => import('@/pages/DocumentsEnhanced'))
const SuppliersPage = lazyRetry(() => import('@/pages/Suppliers'))
const SupplierDetailPage = lazyRetry(() => import('@/pages/SupplierDetail'))
const SupplierComparisonPage = lazyRetry(() => import('@/pages/SupplierComparison'))
const ComponentsPage = lazyRetry(() => import('@/pages/Components'))
const ComponentDetailPage = lazyRetry(() => import('@/pages/ComponentDetail'))
const SupplyChainPage = lazyRetry(() => import('@/pages/SupplyChain'))
const BrandingSettingsPage = lazyRetry(() => import('@/pages/BrandingSettings'))
import { FeatureGate } from '@/components/features/modules'
const QMSDashboardPage = lazyRetry(() => import('@/pages/QMSDashboard'))
const QmsActionsPage = lazyRetry(() => import('@/pages/QmsActions'))
const QualityObjectivesPage = lazyRetry(() => import('@/pages/QualityObjectives'))
const QualityMetricsPage = lazyRetry(() => import('@/pages/QualityMetrics'))
const MrmReviewsPage = lazyRetry(() => import('@/pages/MrmReviews'))
const MrmReviewDetailPage = lazyRetry(() => import('@/pages/MrmReviewDetail'))
const ExternalReferencesPage = lazyRetry(() => import('@/pages/ExternalReferences'))
const NonconformancesPage = lazyRetry(() => import('@/pages/Nonconformances'))
const NonconformanceDetailPage = lazyRetry(() => import('@/pages/NonconformanceDetail'))
const AuditsPage = lazyRetry(() => import('@/pages/Audits'))
const AuditDetailPage = lazyRetry(() => import('@/pages/AuditDetail'))
const ExternalAuditsPage = lazyRetry(() => import('@/pages/ExternalAudits'))
const ExternalAuditDetailPage = lazyRetry(() => import('@/pages/ExternalAuditDetail'))
const ClauseCompliancePage = lazyRetry(() => import('@/pages/ClauseCompliance'))
const ClauseDetailPage = lazyRetry(() => import('@/pages/ClauseDetail'))
const GapAnalysisPage = lazyRetry(() => import('@/pages/GapAnalysis'))
const TeamManagementPage = lazyRetry(() => import('@/pages/TeamManagement'))
const CustomerFeedbackPage = lazyRetry(() => import('@/pages/CustomerFeedback'))
const QmsChangesPage = lazyRetry(() => import('@/pages/QmsChanges'))
const ResourcesPage = lazyRetry(() => import('@/pages/Resources'))
const ImprovementPage = lazyRetry(() => import('@/pages/Improvement'))
const ReportsPage = lazyRetry(() => import('@/pages/Reports'))
const ReportPreviewPage = lazyRetry(() => import('@/pages/ReportPreview'))
const CompanyPage = lazyRetry(() => import('@/pages/Company'))
const SettingsPage = lazyRetry(() => import('@/pages/Settings'))
const HelpPage = lazyRetry(() => import('@/pages/Help'))
const BillingPage = lazyRetry(() => import('@/pages/BillingPage'))
const AdminPage = lazyRetry(() => import('@/pages/AdminPage'))
const PricingPage = lazyRetry(() => import('@/pages/PricingPage'))
const LandingPage = lazyRetry(() => import('@/pages/LandingPage'))
const PrivacyPolicyPage = lazyRetry(() => import('@/pages/PrivacyPolicy'))
const TermsOfServicePage = lazyRetry(() => import('@/pages/TermsOfService'))

function RedirectIfAuthenticated({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth()
  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-muted border-t-certify-600" />
      </div>
    )
  }
  if (user) return <Navigate to="/dashboard" replace />
  return <>{children}</>
}

function NewChecklistRedirect() {
  const [searchParams] = useSearchParams()
  const product = searchParams.get('product')
  return <Navigate to={product ? `/checklists?product=${product}` : '/checklists'} replace />
}

function App() {
  const location = useLocation()

  // Catch unhandled promise rejections that escape React's error boundaries.
  // Reload once per session to recover from blank screens caused by async failures
  // (e.g. failed chunk loads, network errors). Uses sessionStorage to avoid loops.
  useEffect(() => {
    const handler = (event: PromiseRejectionEvent) => {
      console.error('Unhandled promise rejection:', event.reason)

      // Only reload for chunk load errors (e.g. after deployment)
      const err = event.reason instanceof Error ? event.reason : new Error(String(event.reason))
      if (!isChunkLoadError(err)) return

      const key = 'unhandled_rejection_reload'
      if (!sessionStorage.getItem(key)) {
        sessionStorage.setItem(key, '1')
        event.preventDefault()
        window.location.reload()
      }
    }
    window.addEventListener('unhandledrejection', handler)
    return () => window.removeEventListener('unhandledrejection', handler)
  }, [])

  return (
    <ErrorBoundary resetKey={location.pathname}>
      <OfflineIndicator />
      <InstallPrompt />
    <AuthProvider>
      <SubscriptionProvider>
      <BrandingProvider>
      <Suspense fallback={<div className="flex h-screen flex-col items-center justify-center gap-3"><div className="animate-spin rounded-full h-8 w-8 border-2 border-muted border-t-certify-600" /><p className="text-sm text-muted-foreground animate-fade-in">Loading...</p></div>}>
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<SmartHomePage />} />
        <Route path="/login" element={<RedirectIfAuthenticated><LoginPage /></RedirectIfAuthenticated>} />
        <Route path="/signup" element={<RedirectIfAuthenticated><SignupPage /></RedirectIfAuthenticated>} />
        <Route path="/forgot-password" element={<RedirectIfAuthenticated><ForgotPasswordPage /></RedirectIfAuthenticated>} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/accept-invite" element={<AcceptInvitePage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="/terms-of-service" element={<TermsOfServicePage />} />

        {/* Protected routes with dashboard layout */}
        <Route
          element={
            <ProtectedRoute>
                <ViewModeProvider>
                  <OnboardingProvider>
                    <DashboardLayout />
                  </OnboardingProvider>
                </ViewModeProvider>
            </ProtectedRoute>
          }
        >
          <Route path="/dashboard" element={<DashboardPage />} />

          {/* Core - Free tier */}
          <Route path="/documents" element={<DocumentsEnhancedPage />} />
          <Route path="/documents/:id" element={<DocumentsEnhancedPage />} />

          {/* Professional - Manufacturing Suite */}
          <Route
            path="/products"
            element={
              <FeatureGate module="core-products" showUpgrade upgradeMessage="Unlock product management and compliance tracking">
                <ProductsPage />
              </FeatureGate>
            }
          />
          <Route
            path="/products/:id"
            element={
              <FeatureGate module="core-products" showUpgrade upgradeMessage="Unlock product management and compliance tracking">
                <ProductDetailPage />
              </FeatureGate>
            }
          />
          <Route
            path="/markets"
            element={
              <FeatureGate module="core-products" showUpgrade upgradeMessage="Unlock product management and compliance tracking">
                <MarketRequirementsPage />
              </FeatureGate>
            }
          />
          <Route
            path="/checklists"
            element={
              <FeatureGate module="core-checklists" showUpgrade upgradeMessage="Unlock compliance checklists and templates">
                <ChecklistsPage />
              </FeatureGate>
            }
          />
          <Route
            path="/checklists/new"
            element={<NewChecklistRedirect />}
          />
          <Route
            path="/checklists/:id"
            element={
              <FeatureGate module="core-checklists" showUpgrade upgradeMessage="Unlock compliance checklists and templates">
                <ChecklistDetailPage />
              </FeatureGate>
            }
          />
          <Route
            path="/suppliers"
            element={
              <FeatureGate module="supplier-management" showUpgrade upgradeMessage="Unlock supplier tracking and audit management">
                <SuppliersPage />
              </FeatureGate>
            }
          />
          <Route
            path="/suppliers/:id"
            element={
              <FeatureGate module="supplier-management" showUpgrade upgradeMessage="Unlock supplier tracking and audit management">
                <SupplierDetailPage />
              </FeatureGate>
            }
          />
          <Route
            path="/suppliers/compare"
            element={
              <FeatureGate module="supplier-management" showUpgrade upgradeMessage="Unlock supplier tracking and audit management">
                <SupplierComparisonPage />
              </FeatureGate>
            }
          />
          <Route
            path="/components"
            element={
              <FeatureGate module="certificate-manager" showUpgrade upgradeMessage="Unlock component and certificate management">
                <ComponentsPage />
              </FeatureGate>
            }
          />
          <Route
            path="/components/:id"
            element={
              <FeatureGate module="certificate-manager" showUpgrade upgradeMessage="Unlock component and certificate management">
                <ComponentDetailPage />
              </FeatureGate>
            }
          />
          <Route
            path="/supply-chain"
            element={
              <FeatureGate module="supplier-management" showUpgrade upgradeMessage="Unlock supply chain dependency tracking and risk analysis">
                <SupplyChainPage />
              </FeatureGate>
            }
          />

          <Route
            path="/qms"
            element={
              <FeatureGate module="qms" showUpgrade upgradeMessage="Unlock quality management system features">
                <QMSDashboardPage />
              </FeatureGate>
            }
          />
          <Route
            path="/qms/actions"
            element={
              <FeatureGate module="qms" showUpgrade upgradeMessage="Unlock quality management system features">
                <QmsActionsPage />
              </FeatureGate>
            }
          />
          <Route
            path="/qms/objectives"
            element={
              <FeatureGate module="qms" showUpgrade upgradeMessage="Unlock quality management system features">
                <QualityObjectivesPage />
              </FeatureGate>
            }
          />
          <Route
            path="/qms/metrics"
            element={
              <FeatureGate module="qms" showUpgrade upgradeMessage="Unlock quality management system features">
                <QualityMetricsPage />
              </FeatureGate>
            }
          />
          <Route
            path="/qms/mrm"
            element={
              <FeatureGate module="qms" showUpgrade upgradeMessage="Unlock quality management system features">
                <MrmReviewsPage />
              </FeatureGate>
            }
          />
          <Route
            path="/qms/mrm/:id"
            element={
              <FeatureGate module="qms" showUpgrade upgradeMessage="Unlock quality management system features">
                <MrmReviewDetailPage />
              </FeatureGate>
            }
          />
          <Route
            path="/qms/references"
            element={
              <FeatureGate module="qms" showUpgrade upgradeMessage="Unlock quality management system features">
                <ExternalReferencesPage />
              </FeatureGate>
            }
          />
          <Route path="/qms/customer-feedback" element={<FeatureGate module="qms" showUpgrade upgradeMessage="Unlock quality management system features"><CustomerFeedbackPage /></FeatureGate>} />
          <Route path="/qms/changes" element={<FeatureGate module="qms" showUpgrade upgradeMessage="Unlock quality management system features"><QmsChangesPage /></FeatureGate>} />
          <Route
            path="/qms/audits"
            element={
              <FeatureGate module="qms" showUpgrade upgradeMessage="Unlock quality management system features">
                <AuditsPage />
              </FeatureGate>
            }
          />
          <Route
            path="/qms/audits/:id"
            element={
              <FeatureGate module="qms" showUpgrade upgradeMessage="Unlock quality management system features">
                <AuditDetailPage />
              </FeatureGate>
            }
          />
          <Route
            path="/qms/external-audits"
            element={
              <FeatureGate module="qms" showUpgrade upgradeMessage="Unlock quality management system features">
                <ExternalAuditsPage />
              </FeatureGate>
            }
          />
          <Route
            path="/qms/external-audits/:id"
            element={
              <FeatureGate module="qms" showUpgrade upgradeMessage="Unlock quality management system features">
                <ExternalAuditDetailPage />
              </FeatureGate>
            }
          />
          <Route
            path="/qms/compliance"
            element={
              <FeatureGate module="qms" showUpgrade upgradeMessage="Unlock quality management system features">
                <ClauseCompliancePage />
              </FeatureGate>
            }
          />
          <Route
            path="/qms/compliance/:clauseId"
            element={
              <FeatureGate module="qms" showUpgrade upgradeMessage="Unlock quality management system features">
                <ClauseDetailPage />
              </FeatureGate>
            }
          />
          <Route
            path="/qms/compliance/gap-analysis"
            element={
              <FeatureGate module="qms" showUpgrade upgradeMessage="Unlock quality management system features">
                <GapAnalysisPage />
              </FeatureGate>
            }
          />
          <Route
            path="/qms/compliance/gap-analysis/new"
            element={
              <FeatureGate module="qms" showUpgrade upgradeMessage="Unlock quality management system features">
                <GapAnalysisPage />
              </FeatureGate>
            }
          />
          <Route
            path="/qms/compliance/gap-analysis/:id"
            element={
              <FeatureGate module="qms" showUpgrade upgradeMessage="Unlock quality management system features">
                <GapAnalysisPage />
              </FeatureGate>
            }
          />
          <Route
            path="/qms/ncrs"
            element={
              <FeatureGate module="qms" showUpgrade upgradeMessage="Unlock quality management system features">
                <NonconformancesPage />
              </FeatureGate>
            }
          />
          <Route
            path="/qms/ncrs/:id"
            element={
              <FeatureGate module="qms" showUpgrade upgradeMessage="Unlock quality management system features">
                <NonconformanceDetailPage />
              </FeatureGate>
            }
          />
          <Route path="/qms/resources" element={<ResourcesPage />} />
          <Route path="/qms/improvement" element={<ImprovementPage />} />

          <Route path="/team" element={<TeamManagementPage />} />
          <Route path="/company" element={<CompanyPage />} />
          <Route path="/settings/branding" element={<BrandingSettingsPage />} />
          <Route
            path="/reports"
            element={
              <FeatureGate module="qms" showUpgrade upgradeMessage="Unlock compliance reporting features">
                <ReportsPage />
              </FeatureGate>
            }
          />
          <Route
            path="/reports/:id"
            element={
              <FeatureGate module="qms" showUpgrade upgradeMessage="Unlock compliance reporting features">
                <ReportPreviewPage />
              </FeatureGate>
            }
          />
          <Route path="/billing" element={<BillingPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/settings/profile" element={<Navigate to="/settings?tab=account" replace />} />
          <Route path="/help" element={<HelpPage />} />
          <Route path="/admin" element={<AdminPage />} />
        </Route>

        {/* Catch-all redirect */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </Suspense>
      <Toaster />
      </BrandingProvider>
      </SubscriptionProvider>
    </AuthProvider>
    </ErrorBoundary>
  )
}

// Landing page component

function SmartHomePage() {
  const { user, loading } = useAuth()
  if (loading) {
    return <div className="flex h-screen items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-certify-600" /></div>
  }
  if (user) {
    return <Navigate to="/dashboard" replace />
  }
  return <LandingPage />
}

export default App
